import { useEffect, useMemo, useState } from "react";
import { History, Loader2, ArrowRight, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, money } from "@/lib/api";
import { SECTIONS } from "@/components/sectionConfig";

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const paras = (t) => (t || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const TextDiff = ({ a, b }) => {
  const pa = paras(a), pb = paras(b);
  const col = (list, other, cls, label) => (
    <div className="space-y-2 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      {list.map((p, i) => <p key={i} className={`text-sm leading-relaxed rounded px-2 py-1 ${other.includes(p) ? "text-slate-400" : cls}`}>{p}</p>)}
    </div>
  );
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{col(pa, pb, "bg-rose-500/10 text-rose-200 line-through decoration-rose-400/60", "Before")}{col(pb, pa, "bg-emerald-500/10 text-emerald-200", "After")}</div>;
};

const ListDiff = ({ a = [], b = [] }) => {
  const removed = a.filter((x) => !b.includes(x)), added = b.filter((x) => !a.includes(x));
  return (
    <ul className="space-y-1 text-sm">
      {removed.map((x, i) => <li key={`r${i}`} className="text-rose-300 line-through decoration-rose-400/60">− {x}</li>)}
      {added.map((x, i) => <li key={`a${i}`} className="text-emerald-300">+ {x}</li>)}
    </ul>
  );
};

const MiniTable = ({ rows = [], columns, currency, label }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</p>
    <table className="w-full text-xs border border-slate-800 rounded">
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-800">{columns.map((c) => <td key={c.key} className="px-2 py-1 text-slate-300">{c.type === "money" ? money(r[c.key], currency) : String(r[c.key] ?? "")}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const SectionDiff = ({ cfg, a = {}, b = {}, currency }) => (
  <div className="rounded-lg border border-slate-800 p-4 space-y-4" data-testid={`diff-section-${cfg.key}`}>
    <p className="font-display font-semibold">{cfg.title}</p>
    {!same(a.content, b.content) && <TextDiff a={a.content} b={b.content} />}
    {cfg.fields.filter((f) => !same(a[f.key], b[f.key])).map((f) => (
      <div key={f.key}>
        <p className="text-xs text-slate-400 mb-1.5">{f.label}</p>
        {f.type === "list" && <ListDiff a={a[f.key]} b={b[f.key]} />}
        {f.type === "metric" && <p className="num text-sm"><span className="text-rose-300">{f.plain ? a[f.key] ?? "—" : money(a[f.key], currency)}</span> <ArrowRight className="inline h-3 w-3" /> <span className="text-emerald-300">{f.plain ? b[f.key] ?? "—" : money(b[f.key], currency)}</span></p>}
        {f.type === "table" && <div className="grid grid-cols-1 md:grid-cols-2 gap-3"><MiniTable rows={a[f.key]} columns={f.columns} currency={currency} label="Before" /><MiniTable rows={b[f.key]} columns={f.columns} currency={currency} label="After" /></div>}
      </div>
    ))}
  </div>
);

export const VersionCompare = ({ open, onOpenChange, studyId, basePath = "/studies", current, initialA, initialB, onSnapshot, onRestore }) => {
  const [restoring, setRestoring] = useState(false);
  const restore = async (vid) => {
    const v = versions.find((x) => x.id === vid);
    if (!v || !window.confirm(`Restore v${v.number} (${v.label})? The current version is saved first, so you can undo this.`)) return;
    setRestoring(true);
    try {
      await onRestore(vid);
      const r = await api.get(`${basePath}/${studyId}/versions`);
      setVersions(r.data); setAId(vid); setBId("current");
    } catch { /* toast shown by caller */ } finally { setRestoring(false); }
  };
  const [versions, setVersions] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("current");
  const [docs, setDocs] = useState({});

  useEffect(() => {
    if (!open) return;
    api.get(`${basePath}/${studyId}/versions`).then((r) => {
      setVersions(r.data);
      setAId(initialA || r.data[0]?.id || "");
      setBId(initialB || "current");
    });
  }, [open, studyId, basePath, initialA, initialB]);

  useEffect(() => {
    [aId, bId].filter((v) => v && v !== "current" && !docs[v]).forEach((v) =>
      api.get(`${basePath}/${studyId}/versions/${v}`).then((r) => setDocs((d) => ({ ...d, [v]: r.data }))));
  }, [aId, bId, basePath, studyId, docs]);

  const pick = (v) => (v === "current" ? current : docs[v]);
  const A = pick(aId), B = pick(bId);
  const changed = useMemo(() => (A && B ? SECTIONS.filter((s) => !same(A.sections?.[s.key], B.sections?.[s.key])) : []), [A, B]);
  const options = [{ id: "current", label: "Current version" }, ...versions.map((v) => ({ id: v.id, label: `v${v.number} · ${v.label} · ${v.created_at?.slice(0, 10)}` }))];
  const sel = (val, set, testId) => (
    <Select value={val} onValueChange={set}>
      <SelectTrigger data-testid={testId} className="bg-slate-950/60 border-slate-700 text-left min-w-0 [&>span]:truncate"><SelectValue placeholder="Select version" /></SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 max-w-[90vw]">{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100vw-2rem)] max-w-5xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="version-compare">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><History className="h-5 w-5 text-emerald-400" />Version history</DialogTitle>
          <DialogDescription>Snapshots are saved automatically before and after every change request.{onSnapshot ? " You can also save one manually." : ""}</DialogDescription>
          {onSnapshot && <button data-testid="save-snapshot-btn" onClick={async () => { await onSnapshot(); const r = await api.get(`${basePath}/${studyId}/versions`); setVersions(r.data); setAId(r.data[0]?.id || ""); }} className="self-start text-xs text-emerald-400 hover:underline">+ Save snapshot of current version</button>}
        </DialogHeader>
        {!versions.length ? <p className="text-sm text-slate-400" data-testid="no-versions">No saved versions yet.</p> : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">{sel(aId, setAId, "version-a")}{sel(bId, setBId, "version-b")}</div>
            {onRestore && (
              <div className="flex flex-wrap gap-2">
                {[aId, bId].filter((v, i, arr) => v && v !== "current" && arr.indexOf(v) === i).map((v) => (
                  <button key={v} data-testid={`restore-version-${v}`} disabled={restoring} onClick={() => restore(v)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 text-xs hover:bg-amber-500/10 disabled:opacity-50">
                    {restoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}Restore v{versions.find((x) => x.id === v)?.number}
                  </button>
                ))}
              </div>
            )}
            {!A || !B ? <Loader2 className="h-5 w-5 animate-spin text-emerald-400" /> : (
              <div className="space-y-4">
                {(A.score !== B.score || A.verdict !== B.verdict) && <p className="text-sm" data-testid="diff-score">Score: <span className="text-rose-300 num">{A.score} ({A.verdict})</span> <ArrowRight className="inline h-3 w-3" /> <span className="text-emerald-300 num">{B.score} ({B.verdict})</span></p>}
                {changed.length === 0 ? <p className="text-sm text-slate-400" data-testid="diff-none">No differences between these versions.</p>
                  : changed.map((cfg) => <SectionDiff key={cfg.key} cfg={cfg} a={A.sections?.[cfg.key]} b={B.sections?.[cfg.key]} currency={current?.input?.currency || "USD"} />)}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
